import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Shield, CheckCircle, Clock, AlertTriangle, RefreshCw, User,
  Calendar, Phone, Copy, Hash, MapPin, Car, ChevronDown, ChevronUp,
  Loader2, XCircle, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending_schedule:        { label: 'Awaiting schedule',    bg: 'bg-gray-100',   text: 'text-gray-600',   border: 'border-gray-200',   Icon: Clock },
  partner_assigned:        { label: 'Partner assigned',     bg: 'bg-blue-50',    text: 'text-blue-700',   border: 'border-blue-200',   Icon: User },
  no_partner_available:    { label: 'Finding partner…',     bg: 'bg-amber-50',   text: 'text-amber-700',  border: 'border-amber-200',  Icon: RefreshCw },
  needs_seller_reassignment:{ label: 'Action required',     bg: 'bg-red-50',     text: 'text-red-700',    border: 'border-red-200',    Icon: AlertTriangle },
  in_progress:             { label: 'Inspection in progress',bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', Icon: Shield },
  submitted:               { label: 'Inspection submitted', bg: 'bg-blue-50',    text: 'text-blue-700',   border: 'border-blue-200',   Icon: CheckCircle },
  verified:                { label: 'Verified',             bg: 'bg-green-50',   text: 'text-green-700',  border: 'border-green-200',  Icon: CheckCircle },
};

const copyText = (text, label = 'Copied!') => {
  navigator.clipboard.writeText(text).then(() => toast.success(label));
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_MAP[status] || STATUS_MAP.pending_schedule;
  const { Icon, label, bg, text } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${bg} ${text}`}>
      <Icon className="h-3 w-3 flex-shrink-0" />
      {label}
    </span>
  );
};

const LiveVerificationCard = ({ vehicle, onRefresh }) => {
  const { token } = useAuth();
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const POLL_MS = 30000;

  const fetchLive = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/seller/verification-live-status/${vehicle.vehicle_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLiveData(r.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [vehicle.vehicle_id, token]);

  useEffect(() => {
    fetchLive();
    const id = setInterval(fetchLive, POLL_MS);
    return () => clearInterval(id);
  }, [fetchLive]);

  const status = liveData?.verification_status || vehicle.verification_status || 'pending_schedule';
  const cfg = STATUS_MAP[status] || STATUS_MAP.pending_schedule;
  const isVerified = liveData?.verified_by_x7autoz || vehicle.verified_by_x7autoz;
  const needsAction = liveData?.needs_reassignment;
  const verificationId = liveData?.verification_id || vehicle.verification_id;
  const partnerName = liveData?.assigned_partner_name || vehicle.assigned_partner_name;
  const assignment = liveData?.assignment;
  const retryCount = liveData?.retry_count || 0;
  const notification = liveData?.seller_notification;

  return (
    <div className={`border rounded-xl overflow-hidden ${needsAction ? 'border-red-300' : cfg.border}`}>
      <div
        className={`px-4 py-3 flex items-center justify-between gap-2 cursor-pointer ${needsAction ? 'bg-red-50' : cfg.bg}`}
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-3 min-w-0">
          {vehicle.images?.[0] ? (
            <img src={vehicle.images[0]} alt="" className="w-10 h-8 object-cover rounded flex-shrink-0" />
          ) : (
            <div className="w-10 h-8 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
              <Car className="h-4 w-4 text-gray-400" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-800 truncate">
              {vehicle.brand_name} {vehicle.model_name}
            </p>
            <p className="text-xs text-gray-500 font-mono truncate">{vehicle.registration_no}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {loading && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
          <StatusPill status={status} />
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 py-4 space-y-3 bg-white border-t border-gray-100">

          {verificationId && (
            <div className="flex items-center justify-between p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
              <div>
                <p className="text-xs text-blue-600 mb-0.5">Verification ID</p>
                <code className="text-sm font-mono font-bold text-blue-800 tracking-wider">{verificationId}</code>
              </div>
              <button
                onClick={() => copyText(verificationId, 'Verification ID copied!')}
                className="text-blue-400 hover:text-blue-700 p-1.5 rounded transition"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          )}

          {isVerified && (
            <div className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">Vehicle verified by X7 Autoz</p>
                <p className="text-xs text-green-600">Verified badge is now showing on your listing</p>
              </div>
            </div>
          )}

          {partnerName && (
            <div className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg space-y-1.5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Partner assigned</p>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-medium text-xs flex-shrink-0">
                  {partnerName.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800">{partnerName}</p>
                  {liveData?.assigned_partner_uid && (
                    <p className="text-xs text-gray-500 font-mono">{liveData.assigned_partner_uid}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {assignment && (
            <div className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg space-y-1.5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Scheduled slot</p>
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-gray-400" />
                  {assignment.scheduled_date || '—'}
                </span>
                <span className="text-gray-400">·</span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-gray-400" />
                  {assignment.scheduled_from && assignment.scheduled_to
                    ? `${assignment.scheduled_from} – ${assignment.scheduled_to}`
                    : '—'}
                </span>
              </div>
            </div>
          )}

          {vehicle.availability_slots?.length > 0 && (
            <div className="p-2.5 bg-gray-50 border border-gray-200 rounded-lg space-y-1.5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Your submitted availability</p>
              <div className="space-y-1">
                {vehicle.availability_slots.map((slot, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                    <span className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-mono text-[10px] flex-shrink-0">{i + 1}</span>
                    <span>{slot.date}</span>
                    <span className="text-gray-400">·</span>
                    <span>{slot.from_time} – {slot.to_time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {vehicle.address && (
            <div className="flex items-start gap-2 text-xs text-gray-600 p-2.5 bg-gray-50 border border-gray-200 rounded-lg">
              <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
              <span>
                {[vehicle.address.street, vehicle.address.city, vehicle.address.state, vehicle.address.pin]
                  .filter(Boolean).join(', ')}
              </span>
            </div>
          )}

          {notification && (
            <div className={`flex items-start gap-2 p-2.5 text-xs rounded-lg border ${needsAction ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <p>{notification}</p>
            </div>
          )}

          {retryCount > 0 && !isVerified && (
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {retryCount} assignment attempt{retryCount > 1 ? 's' : ''} made · auto-retrying every 2 min
            </p>
          )}

          <div className="flex items-center justify-between pt-1">
            <p className="text-[10px] text-gray-400">Auto-refreshes every 30s</p>
            <button
              onClick={e => { e.stopPropagation(); fetchLive(); }}
              className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 transition"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const VehicleVerificationTab = () => {
  const { token } = useAuth();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleMap, setScheduleMap] = useState({});

  const fetchVerifications = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/seller/my-verifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = r.data.verifications || [];
      setVerifications(list);

      const maps = {};
      await Promise.all(
        list.map(async v => {
          try {
            const sr = await axios.get(`${API}/seller/verification-status/${v.vehicle_id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            maps[v.vehicle_id] = {
              availability_slots: sr.data.schedule?.availability_slots || [],
              address: sr.data.schedule?.address || null,
            };
          } catch {}
        })
      );
      setScheduleMap(maps);
    } catch {
      toast.error('Failed to load verifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVerifications();
  }, [token]);

  const pending = verifications.filter(v => !v.verified_by_x7autoz);
  const completed = verifications.filter(v => v.verified_by_x7autoz);

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
    </div>
  );

  if (verifications.length === 0) return (
    <div className="text-center py-16 px-4">
      <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
        <Shield className="h-8 w-8 text-blue-400" />
      </div>
      <h3 className="font-medium text-gray-700 mb-1">No verifications yet</h3>
      <p className="text-sm text-gray-500 max-w-xs mx-auto">
        When you opt for vehicle verification during listing, the status will appear here.
      </p>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500">{verifications.length} verification{verifications.length !== 1 ? 's' : ''} total</p>
        </div>
        <button
          onClick={fetchVerifications}
          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1.5 transition"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </div>

      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">In progress</p>
          {pending.map(v => (
            <LiveVerificationCard
              key={v.vehicle_id}
              vehicle={{ ...v, ...(scheduleMap[v.vehicle_id] || {}) }}
            />
          ))}
        </div>
      )}

      {completed.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Completed</p>
          {completed.map(v => (
            <LiveVerificationCard
              key={v.vehicle_id}
              vehicle={{ ...v, ...(scheduleMap[v.vehicle_id] || {}) }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VehicleVerificationTab;